/**
 * PWA 模式检测 + 安装提示。
 *
 * 浏览器 vs PWA 两种运行态：
 *   - standalone (已添加到主屏幕)：无地址栏，安全区生效，体验最佳
 *   - browser：有地址栏 / 工具栏，挤压视口；提示用户「添加到主屏幕」
 *
 * `beforeinstallprompt` 在 React 挂载前就可能 fire，所以监听器在模块加载时
 * 立即注册（main.tsx 顶部 import 本模块即可）。iOS Safari 不支持该事件 —
 * 只能给手动「分享 → 添加到主屏幕」引导。
 */

import { useEffect, useReducer } from 'react'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredPrompt: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((l) => l())

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  deferredPrompt = e as BeforeInstallPromptEvent
  notify()
})
window.addEventListener('appinstalled', () => {
  deferredPrompt = null
  notify()
})

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  )
}

export function isIos(): boolean {
  const ua = navigator.userAgent
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ 把自己报成 Mac，靠触点数区分
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export async function promptInstall(): Promise<boolean> {
  if (!deferredPrompt) return false
  await deferredPrompt.prompt()
  const { outcome } = await deferredPrompt.userChoice
  deferredPrompt = null
  notify()
  return outcome === 'accepted'
}

export interface PwaInstallState {
  isStandalone: boolean
  isIos: boolean
  canPrompt: boolean
  promptInstall: () => Promise<boolean>
}

/** 订阅安装态变化（beforeinstallprompt / appinstalled）。*/
export function usePwaInstall(): PwaInstallState {
  const [, force] = useReducer((x: number) => x + 1, 0)
  useEffect(() => {
    listeners.add(force)
    return () => {
      listeners.delete(force)
    }
  }, [])
  return {
    isStandalone: isStandalone(),
    isIos: isIos(),
    canPrompt: deferredPrompt !== null,
    promptInstall,
  }
}
